import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Tabs,
  Tab,
  TextField
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  unshareAllFilesForEmailRoutine,
  fetchSharedUsersForFilesRoutine
} from '../ducks/actions'
import { selectBulkUnshareStatus } from '../ducks/selectors'
import { selectBpFilesList } from 'ducks/files/selectors'

const UnshareAllForEmailModal = ({ open, onClose, bpId }) => {
  const [selectedEmails, setSelectedEmails] = useState([])
  const [tab, setTab] = useState(0)
  const [search, setSearch] = useState('')
  const dispatch = useDispatch()
  const { loading } = useSelector(selectBulkUnshareStatus)
  const files = useSelector(selectBpFilesList)
  const sharedExternals = useSelector(state => state.bpDetails.sharedExternals || [])
  const sharedInternals = useSelector(state => state.bpDetails.sharedInternals || [])

  useEffect(() => {
    if (open && files.length > 0) {
      const fileIds = files.map(f => f.id)
      dispatch(fetchSharedUsersForFilesRoutine.trigger(fileIds))
    }
  }, [open, files, dispatch])

  const externalEmails = sharedExternals.map(u => ({ email: u.email, label: u.email }))
  const internalEmails = sharedInternals.map(u => ({
    email: u.email,
    user_id: u.user_id ?? u.id,
    label: u.firstName || u.lastName ? `${u.firstName || ''} ${u.lastName || ''} (${u.email})` : u.email
  }))

  const displayedEmails = (tab === 0 ? internalEmails : externalEmails).filter(({ label }) =>
    label.toLowerCase().includes(search.toLowerCase())
  )

  const handleToggle = (email) => {
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    )
  }

  const handleSelectAll = () => {
    const allEmails = (tab === 0 ? internalEmails : externalEmails).map(e => e.email)
    setSelectedEmails(allEmails)
  }

  const handleDeselectAll = () => {
    setSelectedEmails([])
  }

  const handleSubmit = () => {
    if (!selectedEmails.length) return

    const payload = { bp_id: bpId }

    if (tab === 0) {
      payload.internals = sharedInternals
        .filter(u => selectedEmails.includes(u.email))
        .map(u => u.user_id ?? u.id)
        .filter(Boolean)
    } else {
      payload.externals = sharedExternals
        .filter(u => selectedEmails.includes(u.email))
        .map(u => u.email)
        .filter(Boolean)
    }

    if (
      (!payload.internals || payload.internals.length === 0) &&
      (!payload.externals || payload.externals.length === 0)
    ) {
      alert('No valid users selected.')
      return
    }

    dispatch(unshareAllFilesForEmailRoutine.trigger(payload))
    handleClose()
  }

  const handleClose = () => {
    setSelectedEmails([])
    setSearch('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Unshare all files for selected email addresses</DialogTitle>
      <DialogContent>
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          textColor="error"
          indicatorColor="error"
        >
          <Tab
            label="Internal Users"
            sx={{ '&.Mui-selected': { color: theme => theme.palette.error.main } }}
          />
          <Tab
            label="External Users"
            sx={{ '&.Mui-selected': { color: theme => theme.palette.error.main } }}
          />
        </Tabs>
        <TextField
          fullWidth
          margin="normal"
          variant="outlined"
          placeholder="Search emails"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {displayedEmails.length > 0 ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: 8 }}>
              <Button size="small" onClick={handleSelectAll} sx={{ color: theme => theme.palette.error.main }}>Select all</Button>
              <Button size="small" onClick={handleDeselectAll} sx={{ color: theme => theme.palette.error.main }}>Deselect all</Button>
            </div>
            <FormGroup>
              {displayedEmails.map(({ email, label }) => (
                <FormControlLabel
                  key={email}
                  control={
                    <Checkbox
                      checked={selectedEmails.includes(email)}
                      onChange={() => handleToggle(email)}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          </>
        ) : (
          <Typography>No shared {tab === 0 ? 'internal' : 'external'} emails found.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading} sx={{ color: theme => theme.palette.error.main }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="error"
          variant="contained"
          disabled={selectedEmails.length === 0 || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Unshare All'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UnshareAllForEmailModal
